import {
  action,
  computed,
  makeObservable,
  observable,
  reaction,
  runInAction,
} from 'mobx'
import { PublicUser } from '../models/PublicUser'
import type { RoomState } from '../models/RoomState'
import type { RoomStateRubricResult } from '../models/RoomStateRubricResult'
import { Section, SectionStatus } from '../models/Section'
import type { SlideRubric } from '../models/SlideRubric'
import type {
  InvitationType,
  RoomStateVideoMethod,
  SectionPaymentMethod,
} from '../types'
import {
  InstructorSectionView,
  SectionRequestState,
  SectionState,
  AssignmentStatus,
} from '../types'

import { StaticModelCollection } from '../firestore-mobx/model'
import { createStudentInvitation } from '../firestore/Invitation'
import {
  getSection,
  setSectionInvoiced,
  setSectionIsTest,
  setSectionNotInvoiced,
  setSectionPassPriceForSection,
  setSectionPassQuantityForSection,
  updateSectionPaymentMethod,
  setSectionState,
  updateSection,
  updateSectionVideoMethod,
  deleteSection,
  removeStudentFromSection,
  restoreStudentToSection,
} from '../firestore/Section'
import {
  getSectionAssignments,
  touchAssignment,
} from '../firestore/SectionAssignment'
import {
  fetchSlideDeck,
  getSlideDeck,
  getSlideDecksForCatalog,
} from '../firestore/SlideDeck'
import type { FirebaseRepository } from '../models/FirebaseRepository'
import { SectionAssignment } from '../models/SectionAssignment'
import { SlideDeck } from '../models/SlideDeck'
import { InstructorAssignmentCubit } from './InstructorAssignmentCubit'
import { Cubit } from './core'
import { Catalog } from '../models/Catalog'
import { getCatalogsForUser } from '../firestore/Catalog'
import { RoomStateEngagement } from '../models/RoomStateEngagement'
import { type UserPromotion } from '../models/UserPromotion'
import {
  applyApprovedFreeAdminPromotion,
  redeemPromotions,
} from '../firestore/UserPromotionRedemption'
import { SectionRequest } from '../models/SectionRequest'
import type { FirestoreSectionRequest } from '../firestore/SectionRequest'
import {
  createSectionInvoiceRequest,
  getSectionRequestsBySectionId,
  SectionRequestType,
  withdrawSectionRequest,
} from '../firestore/SectionRequest'
import type { Organization } from '../models/Organization'
import { getOrganization } from '../firestore/Organization'
import { SectionPromotion } from '../models/SectionPromotion'
import { getSectionPromotions } from '../firestore/SectionPromotion'
import { captureException } from '@sentry/core'
import {
  createSectionPassCoupons,
  getSectionPassCouponsForSection,
} from '../firestore/SectionPassCoupon'
import { SectionPassCoupon } from '../models/SectionPassCoupon'
import { getSettingsSectionPassPricing } from '../firestore/SettingsSectionPassPricing'
import { SettingsSectionPassPricing } from '../models/SettingsSectionPassPricing'
import { type Timestamp } from 'firebase/firestore'
import {
  updateSectionDealDetails,
  updateSectionFreeAccessUntil,
} from '../firestore/Section'
import { assignmentGroupWithRubricDefaults } from '../sources-of-truth/assignmentGroupWithRubricDefaults'
import type { DateTime } from 'luxon'
import type { LoadingState } from '../util/guardOn'
import { guardOn, guardOnLoaded } from '../util/guardOn'
import type { SectionProduct } from '../models/SectionProduct'
import { createSectionProduct } from '../firestore/SectionProduct'
import { fetchUserProfiles } from '../firestore/UserProfile'
import { fetchOptOutStudentData as fetchOptOutStudentDataHelper } from './fetchOptOutStudentData'
import { SectionAssignmentOutput } from '../models/SectionAssignmentOutput'
import { getSectionAssignmentOutputs } from '../firestore/SectionAssignmentOutput'
import type { HttpsCallable } from 'firebase/functions'
import { httpsCallable } from 'firebase/functions'
import { getSectionRoomStateOutputs } from '../firestore/RoomStateOutput'
import { RoomStateOutput } from '../models/RoomStateOutput'
import { compareOptionalDates } from '../util/dates'
import { sortByName, type NameSortMode } from '../util/nameSort'
import { SectionProductController } from './SectionProductController'
import { getOrganizationCustomProducts } from '../firestore/OrganizationCustomProduct'
import { OrganizationCustomProduct } from '../models/OrganizationCustomProduct'

export type SectionAssignmentSortingStrategy =
  | 'dueDate'
  | 'title'
  | 'status'
  | 'grouping'
  | 'grading'

const assignmentStatusSortOrder: Record<AssignmentStatus, number> = {
  [AssignmentStatus.canceled]: 0,
  [AssignmentStatus.unconfigured]: 1,
  [AssignmentStatus.draft]: 2,
  [AssignmentStatus.pending]: 3,
  [AssignmentStatus.in_progress]: 4,
  [AssignmentStatus.completed]: 5,
}

export class InstructorSectionCubit extends Cubit {
  repository: FirebaseRepository
  sectionId: string

  @observable selectedView: InstructorSectionView = InstructorSectionView.cases
  /**
   * Mirror of the URL tab — set by the component via `onTabChanged`. Used
   * internally by stream callbacks (`loadAssignmentCubits`/`initializeCubits`)
   * that need to know which tab is active at the time data loads. The
   * component is the single writer; the URL remains the source of truth.
   */
  @observable private _currentTab: 'cases' | 'students' = 'cases'

  public section: Section
  public assignments: StaticModelCollection<SectionAssignment>
  public allUsers: StaticModelCollection<PublicUser>
  public slideDeckMap = observable.map<string, SlideDeck>({}, { deep: false })
  public catalogs: StaticModelCollection<Catalog>
  public assignmentCubits = observable.map<string, InstructorAssignmentCubit>(
    {},
    { deep: false }
  )

  public sectionPassPricing: SettingsSectionPassPricing

  public sectionInvoiceRequests: StaticModelCollection<SectionRequest>

  @observable
  public sectionOrganization: Organization | null = null

  public organizationCustomProducts: StaticModelCollection<OrganizationCustomProduct>

  @observable
  public promotions: StaticModelCollection<SectionPromotion>

  private instructorUserId?: string

  private _sectionPassCoupons: StaticModelCollection<SectionPassCoupon>
  private assignmentTouchTimestamps = new Map<string, number>()

  // outputs
  public assignmentOutputs: StaticModelCollection<SectionAssignmentOutput>
  public roomStateOutputs: StaticModelCollection<RoomStateOutput>

  customProducts: StaticModelCollection<OrganizationCustomProduct>

  sectionProductsController: SectionProductController

  @observable sortingStrategy: SectionAssignmentSortingStrategy = 'dueDate'
  @observable sortingOrder: 'asc' | 'desc' = 'asc'

  @observable selectedDateField: 'due' | 'available' | 'until' = 'due'

  @observable userSortingOrder: 'asc' | 'desc' = 'asc'
  @observable userNameSortMode: NameSortMode = 'last'

  @observable searchTerm: string = ''
  @observable page: number = 1
  @observable showCancelledAssignments = false

  runRequestedAssignmentRequests: HttpsCallable<
    { sectionId: string },
    { success: boolean }
  >

  runRequestedRoomStateRequest: HttpsCallable<
    { sectionId: string; assignmentId: string },
    { success: boolean }
  >

  swapAssignmentSlideDeckCallable: HttpsCallable<
    { sectionId: string; assignmentId: string; newSlideDeckId: string },
    { success: boolean; error?: string; errorCode?: string }
  >

  pageSize = 20

  constructor(
    repository: FirebaseRepository,
    sectionId: string,
    {
      instructorUserId,
    }: {
      instructorUserId?: string
    } = {}
  ) {
    super()
    makeObservable(this)

    this.repository = repository
    this.sectionId = sectionId
    this.section = Section.empty(repository)
    this.assignments = SectionAssignment.emptyCollection(repository)
    this.allUsers = PublicUser.emptyCollection(repository)
    this.catalogs = Catalog.emptyCollection(repository)
    this.sectionInvoiceRequests = SectionRequest.emptyCollection(repository)
    this.instructorUserId = instructorUserId
    this._sectionPassCoupons = SectionPassCoupon.emptyCollection(repository)
    this.sectionPassPricing = SettingsSectionPassPricing.empty(this.repository)

    this.promotions = SectionPromotion.emptyCollection(repository)
    this.assignmentOutputs = SectionAssignmentOutput.emptyCollection(repository)
    this.roomStateOutputs = RoomStateOutput.emptyCollection(repository)
    this.customProducts = OrganizationCustomProduct.emptyCollection(repository)
    this.organizationCustomProducts =
      OrganizationCustomProduct.emptyCollection(repository)

    this.runRequestedAssignmentRequests = httpsCallable<
      { sectionId: string },
      { success: boolean }
    >(this.repository.functions, 'runRequestedAssignmentRequests', {
      timeout: 540 * 1000, // 9 minutes
    })

    this.runRequestedRoomStateRequest = httpsCallable<
      { sectionId: string; assignmentId: string },
      { success: boolean }
    >(this.repository.functions, 'runRequestedRoomStateRequest', {
      timeout: 540 * 1000, // 9 minutes
    })

    this.swapAssignmentSlideDeckCallable = httpsCallable<
      { sectionId: string; assignmentId: string; newSlideDeckId: string },
      { success: boolean; error?: string; errorCode?: string }
    >(this.repository.functions, 'swapAssignmentSlideDeck', {
      timeout: 120 * 1000,
    })

    this.sectionProductsController = new SectionProductController(repository)
  }

  initialize() {
    this.initializeSectionStream()

    this.startAssignmentOutputsStream()

    this.addStream(
      getSettingsSectionPassPricing(this.repository),
      (pricing) => {
        this.sectionPassPricing.replaceModel(pricing)
      }
    )

    this.addStream(
      getSectionAssignments(this.repository, {
        sectionId: this.sectionId,
      }),
      (models) => {
        this.assignments.replaceModels(models)

        this.initializeSectionRequestsStream()
        this.addSlideDeckStreams(models)
        this.loadAssignmentCubits()
      }
    )
  }

  @computed
  get sectionProduct() {
    const user = this.repository.breakoutUser
    const product = user
      ? user.getMostRecentSectionProduct(this.sectionId)
      : ({
          isLoaded: true,
          isLoading: false,
          value: null,
        } as LoadingState<SectionProduct | null>)

    return product
  }

  startAssignmentOutputsStream() {
    this.addStream(
      getSectionRoomStateOutputs(this.repository, {
        sectionId: this.sectionId,
      }),
      (models) => {
        this.roomStateOutputs.replaceModels(models)

        this.loadAssignmentCubits()
      }
    )

    this.addStream(
      getSectionAssignmentOutputs(this.repository, {
        sectionId: this.sectionId,
      }),
      (models) => {
        this.assignmentOutputs.replaceModels(models)

        this.loadAssignmentCubits()
      },
      {
        onError: (error) => {
          console.error('Error loading section assignment outputs', error)
        },
      }
    )
  }

  initializeStudentsView = () => {
    this.assignmentCubits.forEach((cubit) => {
      cubit.initializeAnswers()
    })
  }

  withdrawSectionRequest = async (requestId: string) => {
    await withdrawSectionRequest(this.repository, this.sectionId, requestId)
  }

  createSectionInvoiceRequest = async ({
    sectionId,
    sectionRequest,
  }: {
    sectionId: string
    sectionRequest: Pick<
      FirestoreSectionRequest,
      | 'sectionRequestAssignmentCount'
      | 'sectionRequestStudentCount'
      | 'requestedAt'
      | 'organizationId'
      | 'sectionId'
      | 'updatedByUserId'
    >
  }) => {
    await createSectionInvoiceRequest(this.repository, sectionId, {
      ...sectionRequest,
      userId: this.repository.uid,
      sectionRequestType: SectionRequestType.invoice,
      sectionRequestState: SectionRequestState.pending,
      updatedAt: new Date(),
      requestedAt: new Date(),
    })
  }

  getOrganization = async () => {
    const organizationId = this.section.data.organizationId
    // keyed by id so a changed or cleared organizationId replaces the stream
    const streamName = `organization-stream-${organizationId}`
    if (organizationId && this.hasStream(streamName)) return

    this.removeStreams('organization')
    if (!organizationId) {
      runInAction(() => {
        this.sectionOrganization = null
        this.organizationCustomProducts.replaceModels([])
      })
      return
    }

    this.addStream(
      getOrganization(this.repository, { organizationId }),
      (organization) => {
        runInAction(() => {
          this.sectionOrganization = organization
          this.startOrganizationCustomProductsStream()
        })
      },
      { name: streamName, namespace: 'organization' }
    )
  }

  startOrganizationCustomProductsStream() {
    const organizationId = this.section.data.organizationId
    if (!organizationId) {
      this.organizationCustomProducts.replaceModels([])
      return
    }
    // shares the 'organization' namespace so getOrganization cancels it
    // when the section's organization changes
    const streamName = `organization-custom-products-${organizationId}`
    if (this.hasStream(streamName)) return
    this.addStream(
      getOrganizationCustomProducts(this.repository, {
        organizationId,
      }),
      (models) => {
        this.organizationCustomProducts.replaceModels(models)
      },
      { name: streamName, namespace: 'organization' }
    )
  }

  rebuildAssignmentsCache = async () => {
    return this.runRequestedAssignmentRequests({
      sectionId: this.sectionId,
    })
  }

  @computed
  get someAssignmentHasRubricWeight(): boolean {
    return this.assignments.models.some((a) => a.hasRubricWeight)
  }

  rebuildRoomStatesCache = async () => {
    let success = true
    for (const assignment of this.assignments.models) {
      const result = await this.runRequestedRoomStateRequest({
        sectionId: this.sectionId,
        assignmentId: assignment.id,
      })
      if (!result.data?.success) {
        success = false
      }
    }
    return { data: { success } }
  }

  @computed
  get activeSectionRequest() {
    // active is the latest request that is not withdrawn
    const latestRequest = this.sectionInvoiceRequests.models
      .slice()
      .sort((a, b) => {
        return b.data.requestedAt.getTime() - a.data.requestedAt.getTime()
      })[0]

    if (!latestRequest) return null
    if (
      latestRequest.data.sectionRequestState === SectionRequestState.withdrawn
    ) {
      return null
    }

    return latestRequest
  }

  @computed
  get organizationLoaded() {
    return (
      !this.section.data.organizationId || this.sectionOrganization?.isLoaded
    )
  }

  @computed
  get organizationLoading() {
    return !this.organizationLoaded
  }

  @computed
  get assignmentOutputsById() {
    return new Map(
      this.assignmentOutputs.models.map((output) => [
        output.data.assignmentId,
        output,
      ])
    )
  }

  @computed
  get shouldUseRoomStateOutputs() {
    if (this.assignmentOutputs.isLoading) return false
    if (this.assignments.isLoading) return false

    // every single assignment must have an output
    return this.assignments.length === this.assignmentOutputs.length
  }

  @computed
  get roomStateOutputsByUserId() {
    const map = new Map<string, RoomStateOutput[]>()

    this.roomStateOutputs.models.forEach((output) => {
      const userOutputs = map.get(output.data.userId) || []
      userOutputs.push(output)
      map.set(output.data.userId, userOutputs)
    })

    return map
  }

  @computed
  get pendingSectionRequest() {
    const latestRequest = this.sectionInvoiceRequests.models.find((request) => {
      return (
        request.data.sectionRequestState === SectionRequestState.pending ||
        request.data.sectionRequestState === SectionRequestState.rejected
      )
    })

    return latestRequest || null
  }

  @computed
  get approvedSectionRequest() {
    const latestRequest = this.sectionInvoiceRequests.models.find(
      (request) =>
        request.data.sectionRequestState === SectionRequestState.approved
    )

    return latestRequest || null
  }

  @computed
  get sectionPassCoupons() {
    const streamKey = 'section-pass-coupons'
    if (!this.hasStream(streamKey)) {
      this.addStream(
        getSectionPassCouponsForSection(this.repository, {
          sectionId: this.sectionId,
        }),
        (coupons) => this._sectionPassCoupons.replaceModels(coupons)
      )
    }
    return this._sectionPassCoupons
  }

  @computed
  get sectionPassPaymentEnabled() {
    return (
      this.sectionPassPricing.hasData &&
      this.sectionPassPricing.data.sectionPassPrice > 0
    )
  }

  initializeSectionStream() {
    this.addStream(
      getSection(this.repository, {
        sectionId: this.sectionId,
      }),
      (section) => {
        this.section.replaceModel(section)

        this.loadAssignmentCubits()
        this.addUserStream()
        this.initCatalogAndSlideDeckStreams()
        this.getOrganization()
        this.startSectionPromotionStream()
        this.startCustomProductsStream()
      }
    )
  }

  initializeSectionRequestsStream() {
    const streamKey = `section-requests`
    if (this.hasStream(streamKey) || !this.section.isLoaded) return
    const { shareable, instructorUserId } = this.section.data
    if (shareable && instructorUserId !== this.repository.uid) {
      // mark as loaded
      this.sectionInvoiceRequests.replaceModels([])
      return
    }
    this.addStream(
      getSectionRequestsBySectionId(this.repository, this.sectionId),
      (models) => {
        // Sorted by requestedAt date, descending
        const sortedRequests = models.sort((a, b) => {
          return b.data.requestedAt.getTime() - a.data.requestedAt.getTime()
        })

        runInAction(() => {
          // only store the invoice requests
          const filteredInvoiceRequests = sortedRequests.filter(
            (request) =>
              request.data.sectionRequestType === SectionRequestType.invoice
          )
          this.sectionInvoiceRequests.replaceModels(filteredInvoiceRequests)
        })
      },
      {
        name: streamKey,
      }
    )
  }

  async generateCoupons(couponCodes: string[]) {
    return await createSectionPassCoupons(this.repository, {
      sectionId: this.sectionId,
      couponIds: couponCodes,
    })
  }

  @action
  dispose() {
    this.slideDeckMap.clear()
    this.assignmentCubits.forEach((cubit) => cubit.dispose())
    this.assignmentTouchTimestamps.clear()
    this.sectionProductsController.dispose()
    return super.dispose()
  }

  startSectionPromotionStream(retryCount = 0): void {
    const name = `section-promotions-${this.section.id}`
    if (this.hasStream(name)) return
    this.addStream(
      getSectionPromotions(this.repository, {
        sectionId: this.section.id,
      }),
      (promotions) => {
        runInAction(() => {
          this.promotions.replaceModels(promotions)
        })
      },
      {
        name: name,
        disableCaptureException: true,
        onError: (error) => {
          if (retryCount < 5) {
            setTimeout(() => {
              this.startSectionPromotionStream(retryCount + 1)
            }, 400 * retryCount)
          } else {
            captureException(error)
          }
        },
      }
    )
  }

  startCustomProductsStream(): void {
    const organizationId = this.section.data.organizationId
    if (!organizationId) {
      this.customProducts.replaceModels([])
      return
    }

    this.addStream(
      getOrganizationCustomProducts(this.repository, {
        organizationId,
      }),
      (customProducts) => {
        runInAction(() => {
          this.customProducts.replaceModels(customProducts)
        })
      }
    )
  }

  @action
  changeSelectedView(selectedView: InstructorSectionView) {
    this.selectedView = selectedView
  }

  /**
   * Called by the component via useEffect whenever the URL tab changes.
   * Updates the cubit's internal mirror and kicks off initialization that
   * depends on the active tab.
   */
  @action
  onTabChanged(tab: 'cases' | 'students') {
    this._currentTab = tab
    this.loadAssignmentCubits()
  }

  addUserStream() {
    const data = this.section.data
    if (!data) return

    const userIds = [
      ...data.userIds,
      ...(data.removedUserIds || []),
      data.instructorUserId,
    ]

    // load the users into the user store, which caches them
    const allUsers = this.repository.userStore.getUsers(userIds) // cache users in user store
    this.allUsers.replaceModels(allUsers)
  }

  @action
  addSlideDeckStreams(models: SectionAssignment[]) {
    // v2: read each assignment's deck document from Adonis/Postgres once instead
    // of a live Firestore stream. The repository picks the source from the global
    // deck-source override (the CX toggle).
    const useV2 = this.repository.readsSlideDeckFromV2

    for (const assignment of models) {
      const slideDeckId = assignment.data.slideDeckId

      if (this.slideDeckMap.has(slideDeckId)) {
        continue
      }

      const slideDeck = SlideDeck.empty(this.repository)
      slideDeck.id = slideDeckId
      this.slideDeckMap.set(slideDeckId, slideDeck)

      if (useV2) {
        void this.fetchSlideDeckDocViaApi(slideDeck, slideDeckId, assignment.id)
      } else {
        this.addStream(
          getSlideDeck(this.repository, { slideDeckId }),
          (model) => {
            slideDeck.replaceModel(model)
          }
        )
      }
    }

    // Start a reaction that watches for assignments whose current slide deck
    // has been removed from the assignment's catalog. When detected, fetch
    // slide decks from that catalog so upgrade candidates are discoverable.
    // This is needed because corre/admin users don't have catalog subscriptions,
    // so getCatalogsForUser returns empty and no catalog slide decks are loaded.
    this.startUpgradeCandidateStreams()
  }

  // v2 deck read for the class page: fetch the deck through the slide-deck entry
  // point and drop it into the pre-created SlideDeck model. Only the document
  // fields (name, type, state) are read here. "Deck only" isn't expressible (an
  // empty include can't be sent over the query string, and an omitted one
  // defaults to all six subcollections), so request just `roles`, the smallest
  // subcollection with no signed URLs, and ignore it. On failure, mark the
  // (still empty) deck loaded so the assignment row shows "Not Set" instead of
  // a spinner forever. We only reach here when readsSlideDeckFromV2.
  private async fetchSlideDeckDocViaApi(
    slideDeck: SlideDeck,
    slideDeckId: string,
    assignmentId: string
  ) {
    const markLoaded = () => slideDeck.replaceData(slideDeck.data)

    try {
      const result = await this.repository.fetchSlideDeckForAssignment({
        sectionId: this.sectionId,
        assignmentId,
        slideDeckId,
        include: ['roles'],
        callsite: 'InstructorSectionCubit.fetchSlideDeckDocViaApi',
        reason: 'addSlideDeckStreams',
      })

      // Don't touch MobX state if the cubit was disposed while the request was
      // in flight (navigated away from the class page).
      if (this.isDisposed) return

      if (!result.success) {
        captureException(
          new Error(
            `instructor section slide-deck v2 read failed: ${result.error}`
          ),
          {
            tags: { feature: 'slide-deck-api', errorCode: result.errorCode },
            extra: { sectionId: this.sectionId, assignmentId, slideDeckId },
          }
        )
        markLoaded()
        return
      }

      const model = SlideDeck.buildModelFromApiResponse(
        this.repository,
        result.slideDeck
      )
      runInAction(() => {
        slideDeck.replaceModel(model)
      })
    } catch (e) {
      captureException(e, { tags: { feature: 'slide-deck-api' } })
      if (this.isDisposed) return
      markLoaded()
    }
  }

  private _upgradeCandidateReactionStarted = false

  /**
   * Reactively starts catalog slide deck streams for menu-eligible
   * assignments so that upgrade candidates are available in slideDeckMap.
   * This is needed because admin/corre users don't have catalog
   * subscriptions, so getCatalogsForUser returns empty.
   */
  private startUpgradeCandidateStreams() {
    if (this._upgradeCandidateReactionStarted) return
    this._upgradeCandidateReactionStarted = true

    const disposer = reaction(
      () => {
        // Collect catalog IDs from assignments that are menu-eligible
        // (not completed, 0 grouped participants) so we can load
        // candidate slide decks from those catalogs.
        const catalogIdsNeedingStream: string[] = []
        for (const assignment of this.assignments.models) {
          if (!this.isUpgradeMenuEligible(assignment)) continue

          const catalogId = assignment.data.catalogId
          if (!catalogId) continue

          const streamName = `catalogSlideDecks:${catalogId}`
          if (!this.hasStream(streamName)) {
            catalogIdsNeedingStream.push(catalogId)
          }
        }
        return catalogIdsNeedingStream
      },
      (catalogIds) => {
        for (const catalogId of catalogIds) {
          const streamName = `catalogSlideDecks:${catalogId}`
          if (this.hasStream(streamName)) continue

          this.addStream(
            getSlideDecksForCatalog(this.repository, { catalogId }),
            (slideDecks) => {
              slideDecks.forEach((slideDeck) => {
                runInAction(() => {
                  this.slideDeckMap.set(slideDeck.id, slideDeck)
                })
              })
            },
            { name: streamName }
          )
        }
      }
    )
    this.disposers.push(disposer)
  }

  @action
  loadAssignmentCubits() {
    if (!this.section.isLoaded) return
    if (!this.assignments.isLoaded) return
    if (!this.assignmentOutputs.isLoaded) return
    if (!this.roomStateOutputs.isLoaded) return

    this.assignments.models.forEach((assignment) => {
      if (!this.assignmentCubits.has(assignment.id)) {
        const cubit = new InstructorAssignmentCubit(this.repository, {
          sectionId: this.sectionId,
          assignmentId: assignment.id,
          initialSection: this.section,
          initialSectionAssignment: assignment,
        })
        this.assignmentCubits.set(assignment.id, cubit)
      }
    })

    this.initializeCubits()
  }

  // I broke up this logic because it became so complex that my brain was melting
  // I'm also trying to make it very safe, so old flow runs when outputs were not enabled
  initializeCubits() {
    if (!this.section.isLoaded) return
    if (!this.assignments.isLoaded) return
    if (!this.assignmentOutputs.isLoaded) return
    if (!this.roomStateOutputs.isLoaded) return

    // We're on the studentsTab, initialize if we have no room state outputs
    if (this._currentTab === 'students') {
      if (this.shouldUseRoomStateOutputs) return

      this.safelyInitializeAllCubits({ initializeAnswers: true })
    }

    // on cases tab, initialize any cubits that don't have outputs yet
    if (this._currentTab === 'cases') {
      this.assignmentCubits.forEach((cubit) => {
        const hasAssignmentOutput = this.assignmentOutputsById.get(
          cubit.assignmentId
        )
        if (hasAssignmentOutput) {
          // Even when we have an output, we need to initialize the peer review controller
          // so that peerReviewsExist.isLoaded works correctly in AssignmentTableRowFromOutput
          if (!cubit.initialized) {
            cubit.peerReviewController.initialize()
          }
          return
        }
        if (!cubit.initialized) {
          touchAssignment(this.repository, {
            sectionId: this.sectionId,
            assignmentId: cubit.assignmentId,
          })
          cubit.initialize()
        }
      })
    }
  }

  safelyInitializeAllCubits({
    initializeAnswers,
  }: { initializeAnswers?: boolean } = {}) {
    this.assignmentCubits.forEach((cubit) => {
      if (!cubit.initialized) {
        cubit.initialize()
        if (initializeAnswers) cubit.initializeAnswers()
      }
    })
  }

  buildAssignmentCubit(assignmentId: string, roomStateId?: string) {
    const assignment = this.assignments.models.find(
      (a) => a.id === assignmentId
    )
    if (!assignment) return null

    const cubit = new InstructorAssignmentCubit(this.repository, {
      sectionId: this.sectionId,
      assignmentId: assignment.id,
      initialSection: this.section,
      initialSectionAssignment: assignment,
      roomStateId,
    })

    return cubit
  }

  updateSection = async ({
    className,
    sectionName,
    organizationId,
    sectionPaymentMethod,
    sectionPassPrice,
    expectedStudents,
    sectionStartDate,
    sectionEndDate,
    selectedProduct,
    isTest,
  }: {
    className?: string
    sectionName?: string
    organizationId?: string
    sectionPaymentMethod?: SectionPaymentMethod
    sectionPassPrice?: number
    expectedStudents?: number
    sectionStartDate?: DateTime
    sectionEndDate?: DateTime
    selectedProduct?: { productId: string; isCustom: boolean }
    isTest?: boolean
  }) => {
    await updateSection(this.repository, this.section.id, {
      className,
      sectionName,
      organizationId,
      sectionPaymentMethod,
      sectionPassPrice,
      expectedStudents,
      sectionStartDate,
      sectionEndDate,
      isTest,
    })

    if (selectedProduct?.productId) {
      await createSectionProduct(this.repository, {
        productId: selectedProduct.productId,
        sectionId: this.section.id,
        customProduct: selectedProduct.isCustom,
        currentUserId: this.repository.uid,
      })
    }

    return this.section.id
  }

  updateSectionDealDetails = async ({
    hubspotDealId,
    sectionDealExpiresAt,
  }: {
    hubspotDealId?: string | null
    sectionDealExpiresAt?: Timestamp | null
  }) => {
    await updateSectionDealDetails(this.repository, {
      sectionId: this.section.id,
      hubspotDealId,
      sectionDealExpiresAt,
    })

    return this.section.id
  }

  updateSectionFreeAccessUntil = async (freeAccessUntil: DateTime | null) => {
    await updateSectionFreeAccessUntil(this.repository, {
      sectionId: this.section.id,
      freeAccessUntil,
    })

    return this.section.id
  }

  private initCatalogAndSlideDeckStreams() {
    // admin logic
    const catalogsKey = 'catalogs-stream'

    // if we already have the stream(s), return
    if (this.hasStream(catalogsKey)) return

    const userId = this.instructorUserId || this.repository.uid
    // instructor and TA logic
    this.addStream(
      getCatalogsForUser(this.repository, {
        userId: userId,
      }),
      (catalogs) => {
        const catalogModels = catalogs.map(({ catalog }) => catalog)
        this.catalogs.replaceModels(catalogModels)
        catalogModels.forEach((catalog) => {
          const catalogSlideDeckStreamName = `catalogSlideDecks:${catalog.id}`
          if (this.hasStream(catalogSlideDeckStreamName)) return
          this.addStream(
            getSlideDecksForCatalog(this.repository, { catalogId: catalog.id }),
            (slideDecks) => {
              slideDecks.forEach((slideDeck) => {
                runInAction(() => {
                  this.slideDeckMap.set(slideDeck.id, slideDeck)
                })
              })
            },
            { name: catalogSlideDeckStreamName }
          )
        })
      },
      { name: catalogsKey }
    )
  }

  createStudentInvitation = async ({ type }: { type: InvitationType }) => {
    const doc = await createStudentInvitation(this.repository, {
      sectionId: this.section.id,
      type: type,
    })

    return doc.id
  }

  async closeSection() {
    await setSectionState(this.repository, {
      sectionId: this.section.id,
      sectionState: SectionState.completed,
    })
  }

  async markAsInProgress() {
    await setSectionState(this.repository, {
      sectionId: this.section.id,
      sectionState: SectionState.inProgress,
    })
  }

  async markAsNotStarted() {
    await setSectionState(this.repository, {
      sectionId: this.section.id,
      sectionState: SectionState.notStarted,
    })
  }

  async updateSectionVideoMethod(videoMethod: RoomStateVideoMethod | null) {
    await updateSectionVideoMethod(this.repository, {
      sectionId: this.sectionId,
      videoMethod,
    })
  }

  applyApprovedFreeAdminPromotion = async ({
    userId,
    sectionId,
  }: {
    sectionId: string
    userId: string
  }) => {
    return await applyApprovedFreeAdminPromotion(this.repository, {
      userId,
      sectionId,
    })
  }

  redeemPromotions = async (
    sectionId: string,
    userPromotions: UserPromotion[]
  ) => {
    await Promise.all(
      userPromotions.map((userPromotion) =>
        redeemPromotions(this.repository, {
          userId: userPromotion.data.userId,
          promotionId: userPromotion.data.promotionId,
          userPromotionId: userPromotion.id,
          sectionId,
        })
      )
    )
  }

  setInvoiced = async () => {
    await setSectionInvoiced(this.repository, {
      sectionId: this.section.id,
    })
  }

  setIsTest = async (isTest: boolean) => {
    await setSectionIsTest(this.repository, {
      sectionId: this.section.id,
      isTest,
    })
  }

  setToStudentPay = async () => {
    await setSectionNotInvoiced(this.repository, {
      sectionId: this.section.id,
    })
  }

  updateSectionPassPrice = async (priceInCents: number | null) => {
    return await setSectionPassPriceForSection(this.repository, {
      sectionId: this.sectionId,
      priceInCents: priceInCents,
    })
  }

  updateSectionPassQuantity = async (quantity: number | null) => {
    return await setSectionPassQuantityForSection(this.repository, {
      sectionId: this.sectionId,
      quantity: quantity,
    })
  }

  updateSectionPaymentMethod = async (paymentMethod: SectionPaymentMethod) => {
    return await updateSectionPaymentMethod(this.repository, {
      sectionId: this.sectionId,
      sectionPaymentMethod: paymentMethod,
    })
  }

  removeStudentFromSection = async (studentId: string) => {
    await removeStudentFromSection(this.repository, this.sectionId, studentId)
  }

  restoreStudentToSection = async (studentId: string) => {
    await restoreStudentToSection(this.repository, this.sectionId, studentId)
  }

  @computed
  get slideDecksById() {
    // calling size will trigger the computed to recompute when
    // a new slide deck is added or removed
    return this.slideDeckMap
  }

  /**
   * Wraps the swapAssignmentSlideDeck callable and resets the assignment cubit
   * on success so it re-fetches slide deck data (questions, rubrics, roles, etc.)
   * for the new deck.
   */
  async swapAssignmentSlideDeck(params: {
    sectionId: string
    assignmentId: string
    newSlideDeckId: string
  }) {
    const result = await this.swapAssignmentSlideDeckCallable(params)

    if (result.data.success) {
      this.resetAssignmentCubit(params.assignmentId)
    }

    return result
  }

  /**
   * Disposes the existing assignment cubit and creates a fresh one so that
   * streams (questions, rubrics, room states, etc.) are re-initialized with
   * the current assignment data.
   */
  @action
  private resetAssignmentCubit(assignmentId: string) {
    const existingCubit = this.assignmentCubits.get(assignmentId)
    if (existingCubit) {
      existingCubit.dispose()
      this.assignmentCubits.delete(assignmentId)
    }

    // The assignment model in assignments collection will be updated by
    // the Firestore listener with the new slideDeckId. Re-creating the
    // cubit via loadAssignmentCubits will pick up the fresh data.
    this.loadAssignmentCubits()
  }

  /**
   * Returns true if an assignment meets the basic preconditions for showing
   * the upgrade menu option (corre/admin only):
   * 1. Assignment is not completed
   * 2. No room has a started session (activeSlide is set)
   */
  private isUpgradeMenuEligible(assignment: SectionAssignment): boolean {
    if (assignment.assignmentStatus === AssignmentStatus.completed) return false

    const assignmentCubit = this.assignmentCubits.get(assignment.id)
    if (assignmentCubit) {
      const hasStartedSession = assignmentCubit.roomStates.models.some(
        (room) => room.didStart
      )
      if (hasStartedSession) return false
    }

    return true
  }

  /**
   * Returns true if an assignment is eligible for auto-detecting upgrade
   * candidates. Extends isUpgradeMenuEligible with an additional check:
   * 3. The current slide deck no longer contains the assignment's catalogId
   */
  private isUpgradeEligible(assignment: SectionAssignment): boolean {
    if (!this.isUpgradeMenuEligible(assignment)) return false

    const catalogId = assignment.data.catalogId
    if (!catalogId) return false

    const currentDeck = this.slideDeckMap.get(assignment.data.slideDeckId)
    if (!currentDeck?.isLoaded) return false

    if (currentDeck.data.catalogIds.includes(catalogId)) return false

    return true
  }

  /**
   * Whether each assignment should show the upgrade menu item.
   * Only requires: not completed + no started sessions.
   */
  @computed
  get assignmentUpgradeMenuEligibility(): Map<string, boolean> {
    const result = new Map<string, boolean>()
    for (const assignment of this.assignments.models) {
      result.set(assignment.id, this.isUpgradeMenuEligible(assignment))
    }
    return result
  }

  /**
   * Whether each assignment has auto-detected upgrade candidates available.
   * Used for the up-arrow icon indicator.
   */
  @computed
  get assignmentUpgradeAvailability(): Map<string, boolean> {
    const result = new Map<string, boolean>()

    for (const assignment of this.assignments.models) {
      if (!this.isUpgradeEligible(assignment)) {
        result.set(assignment.id, false)
        continue
      }

      const catalogId = assignment.data.catalogId!
      const currentDeck = this.slideDeckMap.get(assignment.data.slideDeckId)!
      const typeId = currentDeck.data.slideDeckTypeId

      // Check for replacement candidates with same typeId in same catalog
      let found = false
      for (const [deckId, deck] of this.slideDeckMap) {
        if (deckId === currentDeck.id || !deck.isLoaded || !deck.isPublished)
          continue
        if (deck.data.slideDeckTypeId !== typeId) continue
        if (!deck.data.catalogIds.includes(catalogId)) continue
        found = true
        break
      }
      result.set(assignment.id, found)
    }
    return result
  }

  getUpgradeCandidates(assignmentId: string): SlideDeck[] {
    const assignment = this.assignments.models.find(
      (a) => a.id === assignmentId
    )
    if (!assignment) return []
    const currentDeck = this.slideDeckMap.get(assignment.data.slideDeckId)
    if (!currentDeck?.isLoaded) return []
    const catalogId = assignment.data.catalogId
    if (!catalogId) return []

    const typeId = currentDeck.data.slideDeckTypeId
    const candidates: SlideDeck[] = []
    for (const [deckId, deck] of this.slideDeckMap) {
      if (deckId === currentDeck.id || !deck.isLoaded || !deck.isPublished)
        continue
      if (deck.data.slideDeckTypeId !== typeId) continue
      if (!deck.data.catalogIds.includes(catalogId)) continue
      candidates.push(deck)
    }
    return candidates
  }

  async fetchSlideDeckById(slideDeckId: string): Promise<SlideDeck> {
    return fetchSlideDeck(this.repository, { slideDeckId })
  }

  @computed
  get assignmentCount() {
    return this.assignments.models.length
  }

  @computed
  get allAssignmentCubitsLoaded() {
    if (!this.section.isLoaded) return false
    if (!this.assignments.isLoaded) return false

    const cubits = Array.from(this.assignmentCubits.values())

    return cubits.every((cubit) => cubit.allDataLoaded)
  }

  @action
  toggleShowCancelledAssignments() {
    this.showCancelledAssignments = !this.showCancelledAssignments
  }

  @computed
  get visibleSectionAssignments() {
    if (this.showCancelledAssignments) return this.sectionAssignments
    return this.sectionAssignments.filter((a) => !a.isCancelled)
  }

  @computed
  get cancelledAssignmentCount() {
    return this.sectionAssignments.filter((a) => a.isCancelled).length
  }

  @computed
  get sectionAssignments() {
    const assignments = this.assignments.models.concat() // Shallow copy

    const getSlideDeck = (assignment: SectionAssignment) =>
      this.slideDeckMap.get(assignment.data.slideDeckId)

    assignments.sort((a: SectionAssignment, b: SectionAssignment) => {
      let comparison = 0
      switch (this.sortingStrategy) {
        case 'dueDate': {
          const pickDate = (sa: SectionAssignment) => {
            switch (this.selectedDateField) {
              case 'available':
                return sa.assignedAtDate
              case 'until':
                return sa.expiresAtDate
              default:
                return sa.dueDateTime
            }
          }
          comparison = compareOptionalDates(pickDate(a), pickDate(b))
          if (comparison === 0) {
            comparison = compareOptionalDates(a.expiresAtDate, b.expiresAtDate)
          }
          break
        }
        case 'title': {
          const aTitle = getSlideDeck(a)?.data?.slideDeckName || ''
          const bTitle = getSlideDeck(b)?.data?.slideDeckName || ''
          comparison = aTitle.localeCompare(bTitle)
          break
        }
        case 'status': {
          const aStatus = assignmentStatusSortOrder[a.assignmentStatus]
          const bStatus = assignmentStatusSortOrder[b.assignmentStatus]
          const fallbackValue = Number.MAX_SAFE_INTEGER
          comparison = (aStatus ?? fallbackValue) - (bStatus ?? fallbackValue)

          if (comparison === 0) {
            comparison = compareOptionalDates(a.dueDateTime, b.dueDateTime)
          }

          if (comparison === 0) {
            comparison = compareOptionalDates(a.expiresAtDate, b.expiresAtDate)
          }
          break
        }
        case 'grouping': {
          const aGroups =
            this.assignmentOutputsById.get(a.id)?.data.groupCount ??
            this.assignmentCubits.get(a.id)?.groupCount ??
            0
          const bGroups =
            this.assignmentOutputsById.get(b.id)?.data.groupCount ??
            this.assignmentCubits.get(b.id)?.groupCount ??
            0
          comparison = aGroups - bGroups

          if (
            comparison === 0 &&
            (this.shouldUseRoomStateOutputs || this.allAssignmentCubitsLoaded)
          ) {
            const ungroupedA =
              this.assignmentOutputsById.get(a.id)?.data.ungroupedUserCount ??
              this.assignmentCubits.get(a.id)?.usersNotInGroup.length ??
              0
            const ungroupedB =
              this.assignmentOutputsById.get(b.id)?.data.ungroupedUserCount ??
              this.assignmentCubits.get(b.id)?.usersNotInGroup.length ??
              0
            comparison = ungroupedA - ungroupedB
          }

          break
        }
        case 'grading': {
          let aVal, bVal
          if (a.isDraft) {
            aVal = -1
          } else if (a.data.assignmentGradingScalars) {
            aVal = 1
          } else {
            aVal = 0
          }
          if (b.isDraft) {
            bVal = -1
          } else if (b.data.assignmentGradingScalars) {
            bVal = 1
          } else {
            bVal = 0
          }
          comparison = aVal - bVal

          break
        }
        default:
          comparison = 0
      }
      return this.sortingOrder === 'asc' ? comparison : -comparison
    })
    return assignments
  }

  @computed
  get assignmentIdToProgress() {
    return guardOn(this.allAssignmentCubitsLoaded, () => {
      return (assignmentId: string) => {
        const cubit = this.assignmentCubits.get(assignmentId)
        if (!cubit) return null
        return cubit.assignmentProgress
      }
    })
  }

  @computed
  get sectionUserDataWithRubricDefaults() {
    return guardOn(
      this.section.isLoaded &&
        this.assignments &&
        (this.allAssignmentCubitsLoaded ||
          (this.shouldUseRoomStateOutputs && this.roomStateOutputs.isLoaded)),
      () => {
        const section = this.section.data
        const assignments = this.assignments.models

        const sectionUserData = new Map<string, SectionUserData>()

        // if there are no assignments
        if (assignments.length === 0) {
          this.allUsers.models.forEach((user) => {
            sectionUserData.set(user.id, {
              user,
              roomStates: [],
              assignment: new Map(),
              quizScore: new Map(),
              engagementData: new Map(),
              rubricResults: new Map(),
              isRemoved: false,
            })
          })
        }

        // loop over the assignments
        for (const assignment of assignments) {
          const cubit = this.assignmentCubits.get(assignment.id)
          if (!cubit) continue

          let groupData = cubit.assignmentGroupDataSortedWithNotInGroup

          // Apply rubric defaults of zero score:
          groupData = groupData.map((group) =>
            assignmentGroupWithRubricDefaults({
              assignmentGroupData: group,
              repository: this.repository,
              slideRubrics: cubit.slideRubrics.models,
            })
          )

          const roomStates: RoomState[] = []

          groupData.forEach((group) => {
            if (group.roomState) {
              roomStates.push(group.roomState)
            }
          })

          for (const group of groupData) {
            for (const member of group.groupMembers) {
              const user = this.repository.userStore.getUser(member)

              if (!sectionUserData.has(member)) {
                sectionUserData.set(
                  member,
                  new SectionUserData({
                    user,
                    roomStates,
                    assignment: new Map(),
                    quizScore: new Map(),
                    engagementData: new Map(),
                    rubricResults: new Map(),
                    isRemoved:
                      section.removedUserIds?.includes(member) || false,
                  })
                )
              }

              const memberData = sectionUserData.get(member)
              if (memberData && group.roomState !== undefined) {
                if (group.roomState.isEmpty) {
                  const {
                    assignment,
                    quizScore,
                    engagementData,
                    rubricResults,
                  } = memberData
                  // todo: why does dart app include the empty room state results ?????
                  // should we just omit these ??
                  // in dart we the roomState.empty is a static and therefore
                  // we can only have one empty room in the map. empty room is not static in js
                  // so we delete all empty rooms from the map before setting a new one to maintain behavior
                  const removeEmptyRooms = (map: Map<RoomState, unknown>) => {
                    Array.from(map.keys()).forEach((roomState) => {
                      if (!roomState.isEmpty) return
                      map.delete(roomState)
                    })
                  }
                  ;[
                    assignment,
                    quizScore,
                    engagementData,
                    rubricResults,
                  ].forEach(removeEmptyRooms)
                }
                memberData.assignment.set(group.roomState, assignment)
                memberData.quizScore.set(
                  group.roomState!,
                  group.quizScore.get(member!) || 0.0
                )
                memberData.engagementData.set(
                  group.roomState,
                  group.engagementData.get(member!) ||
                    RoomStateEngagement.empty(this.repository)
                )
                memberData.rubricResults.set(
                  group.roomState,
                  group.rubricResults.get(member!) || new Map()
                )
              }
              if (memberData) {
                sectionUserData.set(member, memberData)
              }
            }
          }
        }

        // add removed users that are not in any assignment
        for (const user of this.allUsers.models) {
          if (!sectionUserData.has(user.id)) {
            const removedUserIds = this.section.data.removedUserIds || []
            const isRemoved = removedUserIds.includes(user.id)
            if (isRemoved) {
              sectionUserData.set(
                user.id,
                new SectionUserData({
                  user,
                  roomStates: [],
                  assignment: new Map(),
                  quizScore: new Map(),
                  engagementData: new Map(),
                  rubricResults: new Map(),
                  isRemoved: isRemoved,
                })
              )
            }
          }
        }

        return sectionUserData
      }
    )
  }

  @computed
  get usersFiltered() {
    return this.section.studentsIncludingRemoved.filter((user) => {
      const fullName = user.fullName
      if (!fullName) return false
      return this.searchTerm.trim().length > 0
        ? fullName.toLowerCase().includes(this.searchTerm.trim().toLowerCase())
        : true
    })
  }

  @computed
  get usersFilteredAndSorted() {
    return sortByName(
      this.usersFiltered,
      this.userNameSortMode,
      (user) => ({ first: user.data.firstName, last: user.data.lastName }),
      this.userSortingOrder
    )
  }

  @computed
  get usersFilteredAndSortedAndPaginated() {
    const start = (this.page - 1) * this.pageSize
    const end = start + this.pageSize
    return this.usersFilteredAndSorted.slice(start, end)
  }

  @computed
  get totalUserPages() {
    return Math.max(1, Math.ceil(this.usersFiltered.length / this.pageSize))
  }

  @computed
  get sectionUserDataWithRubricDefaultsSorted() {
    return guardOnLoaded(
      this.sectionUserDataWithRubricDefaults,
      (sectionUserData) => {
        return sortByName(
          Array.from(sectionUserData.values()),
          this.userNameSortMode,
          (data) => ({
            first: data.user.data.firstName,
            last: data.user.data.lastName,
          }),
          this.userSortingOrder
        )
      }
    )
  }

  @computed
  get sectionUserDataWithRubricDefaultsFiltered() {
    return guardOnLoaded(
      this.sectionUserDataWithRubricDefaultsSorted,
      (sectionData) => {
        return sectionData.filter((data) => {
          const allUserIds = [
            ...this.section.data.userIds,
            ...(this.section.data.removedUserIds || []),
          ]
          const isSectionUser = allUserIds.includes(data.user.id)
          const fullName = data.user.fullName
          if (!fullName || !isSectionUser) return false
          return this.searchTerm.trim().length > 0
            ? fullName
                .toLowerCase()
                .includes(this.searchTerm.trim().toLowerCase())
            : true
        })
      }
    )
  }

  @action
  setPage(page: number) {
    this.page = page
  }

  @action
  setSearchTerm(term: string) {
    this.searchTerm = term
    this.page = 1
  }

  @computed
  get sectionUserDataWithRubricDefaultsFilteredAndPaged() {
    const data = this.sectionUserDataWithRubricDefaultsFiltered
    return guardOn(!data.isLoading, () => {
      const startIndex = (this.page - 1) * this.pageSize
      return data.value!.slice(startIndex, startIndex + this.pageSize)
    })
  }

  @computed
  get pageCount() {
    const data = this.sectionUserDataWithRubricDefaultsFiltered
    const count = data.value?.length ?? 0
    return Math.ceil(count / this.pageSize)
  }

  @computed
  get userCount() {
    return this.section.data.userIds.length
  }

  @computed
  get userWithDeletedCount() {
    return this.section.data.removedUserIds
      ? this.section.data.removedUserIds.length + this.userCount
      : this.userCount
  }

  @action
  changeSortingStrategy = (
    strategy: SectionAssignmentSortingStrategy,
    order: 'asc' | 'desc'
  ) => {
    this.sortingStrategy = strategy
    this.sortingOrder = order
  }

  @action
  changeSelectedDateField = (field: 'due' | 'available' | 'until') => {
    this.selectedDateField = field
  }

  @action
  changeUserSortingOrder = (order: 'asc' | 'desc') => {
    this.userSortingOrder = order
  }

  @action
  changeUserNameSortMode = (mode: NameSortMode) => {
    this.userNameSortMode = mode
  }

  @computed
  get sectionWithStatusAndAssignments() {
    return guardOn(this.section.isLoaded && this.assignments.isLoaded, () => {
      const section = this.section
      const assignments = this.assignments.models

      const latestInvoiceRequest = this.activeSectionRequest
      const sectionStatus = this.section.getSectionStatus({
        assignments,
        latestInvoiceRequest,
      })

      return { section, assignments, sectionStatus }
    })
  }

  async deleteSection() {
    await deleteSection(this.repository, this.sectionId)
  }

  @computed
  get userHasAccessToSection() {
    const currentUser = this.repository.breakoutUser
    if (!currentUser) return false

    return (
      this.section.data.instructorUserId === this.repository.uid ||
      this.section.data.collaboratorRWUserIds.includes(this.repository.uid) ||
      currentUser.isAssistantOfInstructor(this.section.data.instructorUserId)
    )
  }

  @computed
  get userCanAddAssignments() {
    if (this.userHasAccessToSection) return true

    const currentUser = this.repository.breakoutUser
    if (!currentUser) return false

    // If corre AND impersonating, allow adding assignments
    if (currentUser.isCorre && this.instructorUserId) return true

    return false
  }

  @computed
  get showCorreCTA() {
    if (this.userHasAccessToSection) return false

    const currentUser = this.repository.breakoutUser
    if (!currentUser) return false

    if (currentUser.isCorre && !this.instructorUserId) return true

    return false
  }

  @computed
  get studentsTabEnabled() {
    return guardOnLoaded(
      this.sectionWithStatusAndAssignments,
      ({ sectionStatus }) => {
        return (
          (this.sectionAssignments.some((a) => !a.isDraft) ||
            this.section.studentsIncludingRemovedCount > 0) &&
          sectionStatus !== SectionStatus.invoicePending
        )
      }
    )
  }

  fetchUserProfilesForCorreCSV = (userIds: string[]) => {
    if (!this.repository.breakoutUser?.isCorre)
      throw new Error('method not supported for non-corre users')
    return fetchUserProfiles(this.repository, { userIds })
  }

  fetchOptOutStudentData = async () => {
    if (!this.repository.breakoutUser?.isCorre)
      throw new Error('method not supported for non-corre users')

    return fetchOptOutStudentDataHelper(this.repository, {
      sectionId: this.section.id,
      optOutUserIds: this.section.data.optOutUserIds ?? [],
      functions: this.repository.functions,
      firestore: this.repository.firestore,
    })
  }

  duplicateSectionProduct = async (sectionProduct: SectionProduct) => {
    await createSectionProduct(this.repository, {
      productId: sectionProduct.data.productId,
      sectionId: sectionProduct.data.sectionId,
      customProduct: sectionProduct.data.customProduct,
      currentUserId: sectionProduct.data.userId,
    })
  }
}

export class SectionUserData {
  public assignment: Map<RoomState, SectionAssignment>
  public user: PublicUser
  public quizScore: Map<RoomState, number>
  public roomStates: RoomState[]
  public engagementData: Map<RoomState, RoomStateEngagement>
  public rubricResults: Map<
    RoomState,
    Map<SlideRubric, RoomStateRubricResult[]>
  >

  public isRemoved: boolean

  constructor(params: {
    user: PublicUser
    roomStates: RoomState[]
    assignment?: Map<RoomState, SectionAssignment>
    quizScore?: Map<RoomState, number>
    rubricResults?: Map<RoomState, Map<SlideRubric, RoomStateRubricResult[]>>
    engagementData?: Map<RoomState, RoomStateEngagement>
    isRemoved: boolean
  }) {
    this.user = params.user
    this.assignment = params.assignment || new Map()
    this.quizScore = params.quizScore || new Map()
    this.roomStates = params.roomStates
    this.rubricResults = params.rubricResults || new Map()
    this.engagementData = params.engagementData || new Map()
    this.isRemoved = params.isRemoved
  }
}
